import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/App";

/**
 * User State Machine: LOADING | NEW_USER | RETURNING_USER
 * - LOADING -> NEW_USER: userId is "new" or the API returns no completed profile
 * - LOADING -> RETURNING_USER: API confirms onboarding is complete
 * - NEW_USER -> RETURNING_USER: after onboarding completes
 */
export const USER_STATE = { LOADING: "LOADING", NEW_USER: "NEW_USER", RETURNING_USER: "RETURNING_USER" };

const initialState = (userId) => {
  if (userId === "new") return USER_STATE.NEW_USER;
  const isAuth = localStorage.getItem("isAuthenticated") === "true";
  const done = localStorage.getItem(`onboardingComplete_${userId}`) === "true";
  return isAuth && done ? USER_STATE.RETURNING_USER : USER_STATE.LOADING;
};

const fetchProfile = async (userId) => {
  try {
    return (await axios.get(`${API}/users/${userId}`)).data;
  } catch (error) {
    console.error("[CommandModule] Error fetching profile:", error);
    return null;
  }
};

export const useUserState = (userId, navigate) => {
  const [userState, setUserState] = useState(() => initialState(userId));
  const [userData, setUserData] = useState(null);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);

  const fetchUserProfile = useCallback(async () => {
    if (!userId || userId === "new") return;
    const data = await fetchProfile(userId);
    if (data) setUserData(data);
  }, [userId]);

  const markReturning = useCallback(() => {
    setUserState(USER_STATE.RETURNING_USER);
    localStorage.setItem(`onboardingComplete_${userId}`, "true");
    fetchUserProfile();
  }, [userId, fetchUserProfile]);

  const markNew = useCallback(() => {
    setUserState(USER_STATE.NEW_USER);
    setShowOnboardingModal(true);
  }, []);

  // Latest userState for the one-time init effect (must not re-run on every state change)
  const userStateRef = useRef(userState);
  userStateRef.current = userState;

  useEffect(() => {
    const initialize = async () => {
      if (userId === "new") return markNew();
      if (userStateRef.current === USER_STATE.RETURNING_USER) return fetchUserProfile();
      try {
        const { data } = await axios.get(`${API}/auth/check/${userId}`);
        if (data.onboardingComplete && data.hasProfile) markReturning();
        else markNew();
      } catch (error) {
        console.error("[CommandModule] Error checking user status:", error);
        if (error.response?.status === 404) markNew();
        else setUserState(USER_STATE.RETURNING_USER);   // network error: don't block the dashboard
      }
    };
    initialize();
  }, [userId, fetchUserProfile, markNew, markReturning]);

  const completeOnboarding = useCallback(async (newUserId) => {
    const effectiveUserId = newUserId || userId;
    setUserState(USER_STATE.RETURNING_USER);
    setShowOnboardingModal(false);
    localStorage.setItem(`onboardingComplete_${effectiveUserId}`, "true");
    if (newUserId && newUserId !== userId) {
      localStorage.setItem("userId", newUserId);
      navigate(`/dashboard/${newUserId}`);
    }
    if (effectiveUserId && effectiveUserId !== "new") {
      const data = await fetchProfile(effectiveUserId);
      if (data) setUserData(data);
    }
  }, [userId, navigate]);

  const isNewUser = userState === USER_STATE.NEW_USER;
  return {
    userState, userData, isNewUser,
    isLoading: userState === USER_STATE.LOADING,
    shouldShowOnboarding: userId === "new" || (isNewUser && showOnboardingModal),
    openOnboarding: () => setShowOnboardingModal(true),
    closeOnboarding: () => setShowOnboardingModal(false),
    fetchUserProfile, completeOnboarding,
  };
};

// Pings the API every 4 minutes so the backend never idles while the dashboard is open.
export const useKeepAlive = () => {
  useEffect(() => {
    const id = setInterval(() => axios.get(`${API}/`).catch(() => {}), 240000);
    return () => clearInterval(id);
  }, []);
};

// Active workspace, loaded once the user is confirmed as returning.
export const useActiveInstance = (userId, enabled) => {
  const [currentInstance, setCurrentInstance] = useState(null);
  useEffect(() => {
    if (!enabled || !userId || userId === "new") return;
    axios.get(`${API}/instances/${userId}/active`)
      .then((res) => res.data && setCurrentInstance(res.data))
      .catch((error) => console.error("[CommandModule] Error fetching active instance:", error));
  }, [userId, enabled]);
  return [currentInstance, setCurrentInstance];
};
